// Sugalarm — Recent Entries view (cycle 30) + kind filter (cycle 42)
// + day picker (cycle 47).
//
// Secondary screen reached from the Log header's `Recent ›` link (every
// variant) and, cycle 42, from the kiosk/web Log right column's
// `Notes ›` link which deep-links straight to `?kind=note`.
//
// Cycle 47 retires the cycle-31 Calendar surface entirely (the grid
// never fit the 1024×600 kiosk frame and the day-detail drill blanked
// out on kiosk). Navigation collapses onto this single flat list: a
// day-stepper row (prev/next chevrons + a tappable date label that
// opens the native `<input type="date">` + a `Today` reset) drives a
// new `?date=YYYY-MM-DD` URL param (replace, same pattern as the
// cycle-42 `?kind=`). Absent param = today. The list re-renders scoped
// to the picked local calendar day. `?date=` and `?kind=` compose
// freely. No variant-specific code paths — every variant renders the
// same shape; CSS sizing is the only per-surface difference.
//
// State + delete logic remain duplicated from `LogScreen.tsx` on
// purpose; a future refactor can consolidate.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import * as treatmentsApi from "../../api/treatments";
import { BackButton } from "../../components/ui/BackButton";
import { ConfirmDeleteModal } from "../../components/ui/ConfirmDeleteModal";
import { EditTreatmentModal } from "../../components/ui/EditTreatmentModal";
import { ViewEntryModal } from "../../components/ui/ViewEntryModal";
import { useSettings } from "../../hooks/useSettings";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import { fmtEntryTime, localDayKey } from "../../lib/glucose";
import type { Treatment } from "../../lib/types";
import { LogMonthCalendar } from "./LogMonthCalendar";
import logStyles from "./LogScreen.module.css";
import { monthIndex } from "./monthGrid";
import styles from "./RecentEntriesScreen.module.css";

type EventType = Treatment["event_type"];

// Cycle 126: Insulin leads (mirrors the Log-screen type pills + the
// Now-screen QuickLogRow tiles). "All" stays first as the meta-filter.
const KIND_CHIPS: { label: string; type: EventType | null }[] = [
  { label: "All", type: null },
  { label: "Insulin", type: "insulin" },
  { label: "Carbs", type: "carbs" },
  { label: "BG", type: "bg_check" },
  { label: "Notes", type: "note" },
  { label: "Exercise", type: "exercise" },
];

const KNOWN_TYPES: EventType[] = [
  "carbs",
  "insulin",
  "bg_check",
  "note",
  "exercise",
];

const DAY_MS = 86_400_000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` (local key) → local-midnight Date. */
function parseLocalDay(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y ?? NaN, (m ?? 1) - 1, d ?? 1);
}

/** Step a local-day key by ±N calendar days. DST-safe: operates on a
 *  local Date and re-keys through `localDayKey`. */
function shiftDay(key: string, deltaDays: number): string {
  const d = parseLocalDay(key);
  d.setDate(d.getDate() + deltaDays);
  return localDayKey(d);
}

/** ISO-8601 UTC for `<key> 23:59:59.999` local — anchors the server's
 *  `before`-windowed fetch at the end of the picked local day. The
 *  25-hour window (below) plus this end-of-day anchor absorbs DST
 *  forward/backward jumps without dropping edge rows (same shape the
 *  retired single-day calendar hook used). */
function endOfLocalDayIso(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(
    y ?? 0,
    (m ?? 1) - 1,
    d ?? 1,
    23,
    59,
    59,
    999,
  ).toISOString();
}

/** Date-only label — single source of truth for the picker button, the
 *  header title (non-today), and the empty-state copy. Boundary
 *  conventions mirror cycle-29 `fmtEntryTime` (calendar-midnight diffs,
 *  not 24h-from-now deltas) but this is date-only: `Today` /
 *  `Yesterday` / `Tue May 13` (last 6 days) / `May 2, 2026` (older). */
function fmtPickerLabel(dayKey: string, todayKey: string): string {
  if (dayKey === todayKey) return "Today";
  const d = parseLocalDay(dayKey);
  const t = parseLocalDay(todayKey);
  const days = Math.round((t.getTime() - d.getTime()) / DAY_MS);
  if (days === 1) return "Yesterday";
  if (days >= 2 && days <= 6) {
    const weekday = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
    }).format(d);
    const monthDay = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(d);
    return `${weekday} ${monthDay}`;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

// Cycle 203: colorful List / Calendar toggle glyphs (Android-tablet kiosk
// only). Token-driven gradients — `stop-color: var(--token)` resolves in
// the kiosk's Chromium WebView. Unique gradient ids so the two don't clash.
function ListGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="vlListGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--zone-ok)" />
          <stop offset="100%" stopColor="var(--accent)" />
        </linearGradient>
      </defs>
      <g fill="url(#vlListGrad)">
        <circle cx="4" cy="6" r="1.8" />
        <rect x="8" y="4.8" width="12" height="2.4" rx="1.2" />
        <circle cx="4" cy="12" r="1.8" />
        <rect x="8" y="10.8" width="12" height="2.4" rx="1.2" />
        <circle cx="4" cy="18" r="1.8" />
        <rect x="8" y="16.8" width="12" height="2.4" rx="1.2" />
      </g>
    </svg>
  );
}

function CalendarGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="vlCalGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--zone-warn)" />
          <stop offset="100%" stopColor="var(--zone-urgent)" />
        </linearGradient>
      </defs>
      <rect x="3" y="5" width="18" height="16" rx="3" fill="url(#vlCalGrad)" />
      {/* header band — no --accent-2 token exists, so the accent is the band */}
      <path
        d="M3 8a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v1H3z"
        fill="var(--accent)"
      />
      <rect x="7" y="2.5" width="2.4" height="4" rx="1.2" fill="var(--accent)" />
      <rect
        x="14.6"
        y="2.5"
        width="2.4"
        height="4"
        rx="1.2"
        fill="var(--accent)"
      />
      <g fill="var(--bg-elev)">
        <circle cx="8" cy="14" r="1.3" />
        <circle cx="12" cy="14" r="1.3" />
        <circle cx="16" cy="14" r="1.3" />
        <circle cx="8" cy="18" r="1.3" />
        <circle cx="12" cy="18" r="1.3" />
      </g>
    </svg>
  );
}

export function RecentEntriesScreen({ variant }: { variant: FrameVariant }) {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Cycle 203: tablet gate — the View Log / calendar glass redesign (gradient
  // back buttons, colorful toggle icons, glass rows/grid) applies only on the
  // Android-tablet kiosk (the existing nativeEntry gate). Pi/iPad/web/mobile
  // render byte-identical to before (marker absent).
  const nativeEntry = variant === "kiosk" && isAndroidTabletClassRuntime();
  // Cycle 327: the mobile entry list sits on a translucent glass card so the
  // themed wallpaper reads through behind the entries, and the `‹ Log` /
  // `‹ Calendar` back controls take the rounded-rectangle radius of the
  // List | Calendar toggle. Both scoped to `variant === "mobile"`; the
  // kiosk (incl. Android-tablet), iPad, and web Recent layouts are
  // byte-identical (marker absent).
  const isMobile = variant === "mobile";

  // ── Day picker (cycle 47) ─────────────────────────────────────────
  const todayKey = localDayKey(Date.now());
  const floorKey = localDayKey(Date.now() - 365 * DAY_MS);
  const rawDate = searchParams.get("date");
  const rawDateInvalid =
    rawDate != null &&
    (!DATE_RE.test(rawDate) ||
      Number.isNaN(parseLocalDay(rawDate).getTime()));
  const pickedKey: string =
    rawDate == null || rawDateInvalid
      ? todayKey
      : rawDate > todayKey
        ? todayKey // future → clamp to today on read
        : rawDate < floorKey
          ? floorKey // earlier than the 365-day floor → clamp up
          : rawDate;
  const isToday = pickedKey === todayKey;
  const canPrev = pickedKey > floorKey;
  const canNext = !isToday;
  const pickerLabel = fmtPickerLabel(pickedKey, todayKey);

  // Invalid `?date=` is silently treated as today AND scrubbed from the
  // URL (replace, so it doesn't pollute the OS / browser back stack).
  useEffect(() => {
    if (!rawDateInvalid) return;
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    setSearchParams(next, { replace: true });
  }, [rawDateInvalid, searchParams, setSearchParams]);

  // ── Kind filter (cycle 42, unchanged) ─────────────────────────────
  const rawKind = searchParams.get("kind");
  const activeKind: EventType | null =
    rawKind && (KNOWN_TYPES as string[]).includes(rawKind)
      ? (rawKind as EventType)
      : null;
  const notesActive = activeKind === "note";

  // ── Calendar view (cycle 134) ─────────────────────────────────────
  // `?view=calendar` + no date → month grid; `?view=calendar` + date →
  // day-detail (the existing single-day list + a `‹ Calendar` back). No
  // `?view` → the flat single-day list (the default, unchanged).
  const inCalendar = searchParams.get("view") === "calendar";
  const showCalendarGrid = inCalendar && (rawDate == null || rawDateInvalid);
  const showDayDetail = inCalendar && rawDate != null && !rawDateInvalid;

  // Displayed month for the grid, in component state (persists across a
  // day drill-in and back). Seeded to the current month.
  const [displayedMonth, setDisplayedMonth] = useState(() => {
    const t = parseLocalDay(todayKey);
    return { year: t.getFullYear(), month: t.getMonth() };
  });
  const displayedIdx = monthIndex(displayedMonth.year, displayedMonth.month);
  const todayDate = parseLocalDay(todayKey);
  const floorDate = parseLocalDay(floorKey);
  const currentMonthIdx = monthIndex(
    todayDate.getFullYear(),
    todayDate.getMonth(),
  );
  const floorMonthIdx = monthIndex(
    floorDate.getFullYear(),
    floorDate.getMonth(),
  );
  const canPrevMonth = displayedIdx > floorMonthIdx;
  const canNextMonth = displayedIdx < currentMonthIdx;

  const { settings } = useSettings();
  const h24 = settings?.display_24h ?? false;
  const [recent, setRecent] = useState<Treatment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Cycle 34.20: row currently being edited via EditTreatmentModal.
  const [editingRow, setEditingRow] = useState<Treatment | null>(null);
  // Cycle 34.22: row being viewed read-only via ViewEntryModal.
  const [viewingRow, setViewingRow] = useState<Treatment | null>(null);
  // Cycle 42: row pending delete confirmation (gates the `×`).
  const [pendingDelete, setPendingDelete] = useState<Treatment | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Cycle 47: fetch shape keyed off both `?date=` and `?kind=`.
  //  • today + kind ≠ note → 24 h / 200 (cycle-42 behavior).
  //  • today + kind = note → 30 d / 500 (cycle-42 "mini case studies"
  //    widen — preserved exactly).
  //  • any non-today day (regardless of kind) → 25 h anchored at that
  //    day's local end-of-day, then filtered client-side to the picked
  //    local calendar day. Single-day scoping wins — the notes widen
  //    does NOT apply on a non-today day.
  const reload = useCallback(async () => {
    setLoading(true);
    const opts = isToday
      ? notesActive
        ? { hours: 24 * 30, limit: 500 }
        : { hours: 24, limit: 200 }
      : {
          hours: 25,
          before: endOfLocalDayIso(pickedKey),
          limit: 500,
        };
    const r = await treatmentsApi.list(opts);
    setLoading(false);
    if (r.ok) {
      const rows = isToday
        ? r.data.treatments
        : r.data.treatments.filter(
            (t) => localDayKey(t.timestamp) === pickedKey,
          );
      setRecent(rows);
      setError(null);
    } else {
      setError(r.error.message);
    }
  }, [isToday, notesActive, pickedKey]);

  useEffect(() => {
    // The single-day list isn't shown in the month grid — skip its fetch
    // there (the calendar runs its own month-scoped fetch).
    if (showCalendarGrid) return;
    void reload();
  }, [reload, showCalendarGrid]);

  // Keep the displayed month in step with the picked day while in
  // day-detail, so `‹ Calendar` returns to the month you're viewing
  // (incl. a deep-linked `?view=calendar&date=…` or a day-step across a
  // month boundary).
  useEffect(() => {
    if (!showDayDetail) return;
    const d = parseLocalDay(pickedKey);
    if (
      d.getFullYear() !== displayedMonth.year ||
      d.getMonth() !== displayedMonth.month
    ) {
      setDisplayedMonth({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [showDayDetail, pickedKey, displayedMonth.year, displayedMonth.month]);

  const removeOne = async (id: string) => {
    const r = await treatmentsApi.remove(id);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    void reload();
  };

  const setKind = (type: EventType | null) => {
    const next = new URLSearchParams(searchParams);
    if (type == null) next.delete("kind");
    else next.set("kind", type);
    setSearchParams(next, { replace: true });
  };

  const setPicked = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      // Absent param == today (cleanest URL; matches the `Today` reset) —
      // but in calendar mode keep an explicit date even for today, so
      // stepping to today stays in day-detail instead of collapsing to
      // the month grid.
      if (key === todayKey && !inCalendar) next.delete("date");
      else next.set("date", key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, todayKey, inCalendar],
  );

  // ── Calendar view + month-nav handlers (cycle 134) ────────────────
  const setCalendarView = useCallback(
    (on: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (on) next.set("view", "calendar");
      else next.delete("view"); // leave ?date as-is (spec)
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const backToCalendar = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    next.set("view", "calendar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const stepMonth = useCallback((delta: number) => {
    setDisplayedMonth((m) => {
      const d = new Date(m.year, m.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }, []);

  // Tapping the date label opens the platform's native date picker.
  // `showPicker()` where supported; graceful focus()+click() fallback
  // for browsers that lack it. No custom popover.
  const openNativePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    const withPicker = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof withPicker.showPicker === "function") {
      try {
        withPicker.showPicker();
        return;
      } catch {
        /* not allowed in this context — fall through */
      }
    }
    el.focus();
    el.click();
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const t of recent) c[t.event_type] = (c[t.event_type] ?? 0) + 1;
    return c;
  }, [recent]);

  const filtered = useMemo(
    () =>
      activeKind == null
        ? recent
        : recent.filter((t) => t.event_type === activeKind),
    [recent, activeKind],
  );

  const headerTitle = showCalendarGrid
    ? "Calendar"
    : isToday
      ? notesActive
        ? "Notes (30d)"
        : "Recent (24h)"
      : pickerLabel;

  const activeChipLabel =
    KIND_CHIPS.find((c) => c.type === activeKind)?.label ?? "";
  const emptyMsg = isToday
    ? notesActive
      ? "No notes in the last 30 days."
      : "No entries."
    : activeKind == null
      ? `Nothing logged on ${pickerLabel}.`
      : `No ${activeChipLabel} logged on ${pickerLabel}.`;

  // Cycle 327: the entry rows are unchanged (per-kind color, value, note,
  // time, tap → view, ✎ edit, × delete). Extracted so both the mobile
  // glass-card wrapper and the direct kiosk/web render can share them.
  const renderRows = () =>
    filtered.map((tr) => {
      const v =
        tr.event_type === "carbs"
          ? `${tr.carbs_g ?? "—"} g`
          : tr.event_type === "insulin"
            ? `${tr.insulin_u ?? "—"} u`
            : "";
      return (
        <div
          key={tr.id}
          className={`${logStyles.entry} ${logStyles.entryClickable} ${
            nativeEntry ? styles.glassRow : ""
          }`}
          role="button"
          tabIndex={0}
          onClick={() => setViewingRow(tr)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setViewingRow(tr);
            }
          }}
        >
          <span className={logStyles.entryKind}>
            {tr.event_type.replace("_", " ")}
          </span>
          {v && <span className={logStyles.entryValue}>{v}</span>}
          <span className={logStyles.entryNote}>{tr.notes ?? ""}</span>
          <span className={logStyles.entryTime}>
            {fmtEntryTime(tr.timestamp, Date.now(), h24)}
          </span>
          <button
            type="button"
            className={logStyles.entryEdit}
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              setEditingRow(tr);
            }}
          >
            ✎
          </button>
          <button
            type="button"
            className={logStyles.entryDel}
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              setPendingDelete(tr);
            }}
          >
            ×
          </button>
        </div>
      );
    });

  return (
    <div
      className={`${styles.root} ${nativeEntry ? styles.androidTablet : ""} ${
        isMobile ? styles.mobile : ""
      }`}
    >
      <div className={styles.header}>
        {/* Cycle 162: shared BackButton, same nav("/log") handler.
            Cycle 323 (Thread B): the cycle-183 solid-purple (and cycle-203
            gradient on the tablet) is replaced by the QUIET token-based nav
            pill on every variant — same --bg-sunk/--line-strong surface as
            the List | Calendar toggle beside it — so the accent purple is
            reserved for real actions. Navigation to /log is unchanged.
            Cycle 327: on mobile the pill takes the rounded-rectangle radius
            of the List | Calendar toggle beside it (`squared`); kiosk/web
            keep the default pill. */}
        <BackButton
          label="Log"
          onBack={() => nav("/log")}
          quiet
          squared={isMobile}
        />
        <span className={styles.title}>{headerTitle}</span>
        {/* Cycle 134: List | Calendar mode toggle. */}
        <div
          className={styles.viewToggle}
          role="tablist"
          aria-label="View mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!inCalendar}
            className={`${styles.viewSeg} ${
              !inCalendar ? styles.viewSegActive : ""
            }`}
            onClick={() => setCalendarView(false)}
          >
            {nativeEntry && (
              <span className={styles.segIcon} aria-hidden="true">
                <ListGlyph />
              </span>
            )}
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={inCalendar}
            className={`${styles.viewSeg} ${
              inCalendar ? styles.viewSegActive : ""
            }`}
            onClick={() => setCalendarView(true)}
          >
            {nativeEntry && (
              <span className={styles.segIcon} aria-hidden="true">
                <CalendarGlyph />
              </span>
            )}
            Calendar
          </button>
        </div>
      </div>

      {showDayDetail ? (
        <div className={styles.backRow}>
          {/* Cycle 162: shared BackButton; backToCalendar keeps its exact
              return-to-grid behavior. Cycle 323 (Thread B): quiet nav pill
              (matching the `‹ Log` control) — accent only on press. Cycle
              327: rounded-rectangle radius on mobile, for consistency. */}
          <BackButton
            label="Calendar"
            onBack={backToCalendar}
            quiet
            squared={isMobile}
          />
        </div>
      ) : null}

      {!showCalendarGrid && (
      <div className={styles.pickerBar}>
        <div className={styles.pickerRow}>
          <button
            type="button"
            className={styles.chev}
            aria-label="Previous day"
            disabled={!canPrev}
            onClick={() => {
              if (canPrev) setPicked(shiftDay(pickedKey, -1));
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.dateLabelBtn}
            aria-label={`Change date — currently ${pickerLabel}`}
            onClick={openNativePicker}
          >
            {pickerLabel}
          </button>
          <input
            ref={dateInputRef}
            type="date"
            className={styles.dateInput}
            tabIndex={-1}
            aria-hidden="true"
            value={pickedKey}
            min={floorKey}
            max={todayKey}
            onChange={(e) => {
              const v = e.target.value;
              if (
                !DATE_RE.test(v) ||
                Number.isNaN(parseLocalDay(v).getTime())
              ) {
                return;
              }
              const clamped =
                v > todayKey ? todayKey : v < floorKey ? floorKey : v;
              setPicked(clamped);
            }}
          />
          <button
            type="button"
            className={styles.chev}
            aria-label="Next day"
            disabled={!canNext}
            onClick={() => {
              if (canNext) setPicked(shiftDay(pickedKey, 1));
            }}
          >
            ›
          </button>
          <button
            type="button"
            className={styles.todayBtn}
            onClick={() => setPicked(todayKey)}
          >
            Today
          </button>
        </div>
      </div>
      )}

      <div className={styles.kindBar}>
        <div className={styles.kindRow}>
          {KIND_CHIPS.map((chip) => {
            const isActive =
              chip.type == null ? activeKind == null : activeKind === chip.type;
            const count =
              chip.type == null ? recent.length : counts[chip.type] ?? 0;
            return (
              <button
                key={chip.label}
                type="button"
                onClick={() => setKind(chip.type)}
                className={`${styles.kindChip} ${
                  isActive ? styles.kindChipActive : ""
                }`}
              >
                <span className={styles.kindChipLabel}>{chip.label}</span>
                {/* Counts reflect the single-day list; suppressed in the
                    month grid where there's no single day in view. */}
                {!showCalendarGrid && (
                  <span className={styles.kindCount}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {showCalendarGrid ? (
        <LogMonthCalendar
          androidTablet={nativeEntry}
          year={displayedMonth.year}
          month={displayedMonth.month}
          todayKey={todayKey}
          floorKey={floorKey}
          activeKind={activeKind}
          canPrev={canPrevMonth}
          canNext={canNextMonth}
          onPrevMonth={() => {
            if (canPrevMonth) stepMonth(-1);
          }}
          onNextMonth={() => {
            if (canNextMonth) stepMonth(1);
          }}
          onToday={() =>
            setDisplayedMonth({
              year: todayDate.getFullYear(),
              month: todayDate.getMonth(),
            })
          }
          onPickDay={(key) => setPicked(key)}
        />
      ) : (
      <div className={styles.body}>
        {error && <div className={logStyles.errorMsg}>{error}</div>}
        {/* Cycle 327: on mobile the entry list (rows AND the empty/loading
            state) sits on a translucent glass card so the themed wallpaper
            reads through behind the entries; the empty state centers on the
            card. Rows keep their per-kind color / value / note / time and
            their tap → view / edit / delete behavior unchanged (opaque row
            fill). Scoped to `variant === "mobile"` — kiosk/iPad/web render
            the list directly, byte-identical. */}
        {isMobile ? (
          <div className={styles.entriesCard}>
            {loading ? (
              <div className={`${styles.empty} ${styles.emptyOnCard}`}>
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className={`${styles.empty} ${styles.emptyOnCard}`}>
                {emptyMsg}
              </div>
            ) : (
              <div className={styles.list}>{renderRows()}</div>
            )}
          </div>
        ) : loading ? (
          <div className={styles.empty}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>{emptyMsg}</div>
        ) : (
          <div className={styles.list}>{renderRows()}</div>
        )}
      </div>
      )}
      <EditTreatmentModal
        treatment={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={() => {
          void reload();
        }}
      />
      <ViewEntryModal
        treatment={viewingRow}
        onClose={() => setViewingRow(null)}
        h24={h24}
      />
      <ConfirmDeleteModal
        treatment={pendingDelete}
        h24={h24}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) void removeOne(target.id);
        }}
      />
    </div>
  );
}
